import { useEffect, useState, type CSSProperties } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type { NeaAbgang, NeaAnlage, NeaAuslastung, NeaPruefpunkt, NeaPruefzeile, NeaStand } from './elektro/nea-simaris';
import { befundeGegliedert, datum, neaBefundText, PDF_HINWEIS, pruefZustand, zahl, zeitpunkt } from './elektro/nea-simaris';
import './elektro/nea-simaris.css';
import { IconHerunterladen } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, PropertyGrid, StatusBadge,
  type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Netzersatzanlagen (EG4) — J16, 13.10.2026; überarbeitet ES, 16.10.2026.
 *
 * LIEST `GET …/elektro/nea`: je NEA die Bilanz der Quelle mit Nachrechnung,
 * das Datenblatt des Aggregats und das Tankblatt, die Auslastung in BEIDEN
 * Annahmen (F1 offen), die Prüfung des Generators, die Laufzeit des Tanks bei
 * der Last der Bilanz und die Prüfpunkte der Sicherheitsstromversorgung —
 * was die Quelle nicht hergibt, steht als „Daten fehlen“ da.
 *
 * PDF (ELT-NEA) und Excel holt erst ein Klick; der PDF-Abruf legt nur bei geändertem
 * Stand eine neue Berichtsfassung an, sonst die abgelegte (F11).
 */
export function ElektroNea() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <NeaAnsicht api={api} projektId={projekt.id} />;
}

/** Die Felder des Datenblatts in der Reihenfolge des Blattes, mit Einheit. */
const DATENBLATT: [string, string, string?, number?][] = [
  ['typ', 'Typ'],
  ['bemessungsleistungKva', 'Nennleistung Sn', 'kVA', 0],
  ['cosPhi', 'Leistungsfaktor cos φ', undefined, 2],
  ['ueberlastProzent', 'Überlast nach DIN 6280', '%', 0],
  ['spannungV', 'Nennspannung', 'V', 0],
  ['frequenzHz', 'Nennfrequenz', 'Hz', 0],
  ['motorleistungKw', 'Motorleistung (ISO 8528-1)', 'kW', 0],
  ['generatorNennstromA', 'Generatornennstrom', 'A', 2],
  ['leistungsschalterA', 'Leistungsschalter', 'A', 0],
  ['verbrauchVolllastLh', 'Kraftstoffverbrauch 4/4 Last', 'l/h', 1],
  ['spezVerbrauch100', 'spez. Verbrauch 100 % Last', 'g/kWh', 1],
  ['spezVerbrauch75', 'spez. Verbrauch 75 % Last', 'g/kWh', 1],
  ['spezVerbrauch50', 'spez. Verbrauch 50 % Last', 'g/kWh', 1],
  ['tagestankL', 'Tagestank', 'l', 0],
  ['raumMasse', 'Raum L × B × H (mit Schalldämmung)', 'm'],
  ['raumflaecheM2', 'Raumfläche', 'm²', 1],
  ['abgasDn', 'Abgasleitung bis 20 m', 'DN', 0],
  ['zuAbluftM2', 'Zu- und Abluft je Öffnung', 'm²', 1],
  ['gewichtKg', 'Gewicht', 'kg', 0],
];

const TANK: [string, string, string?, number?][] = [
  ['tankvolumenL', 'Tankvolumen laut Blatt', 'l', 1],
  ['tankBasisKw', 'Leistungsbasis P', 'kW', 0],
  ['tankBasisKva', 'daneben im Blatt', 'kVA', 0],
  ['betriebszeitH', 'Betriebszeit t', 'h', 1],
  ['betriebH', 'davon Betrieb', 'h', 1],
  ['testzeitH', 'davon Test (monatlich)', 'h', 1],
  ['reservezeitH', 'davon Reserve', 'h', 1],
  ['tankBeGkwh', 'spez. Verbrauch be (Richtwert)', 'g/kWh', 1],
  ['dichteGl', 'Dichte ρ (gerechnet)', 'g/l', 0],
  ['dichteHinweisKgL', 'Dichte (zweite Angabe im Blatt)', 'kg/l', 3],
  ['tankVerbrauch100Lh', 'Verbrauch 100 % laut Blatt', 'l/h', 2],
  ['tankVerbrauch75Lh', 'Verbrauch 75 % laut Blatt', 'l/h', 2],
  ['tankNachgerechnetL', 'V = P · t · be / ρ (nachgerechnet)', 'l', 2],
  ['tankMitDatenblattL', 'dieselbe Formel mit be des Datenblatts', 'l', 2],
];

function werte(quelle: Record<string, unknown>, felder: [string, string, string?, number?][]) {
  return felder
    .filter(([k]) => k in quelle)
    .map(([k, beschriftung, einheit, nk]) => {
      const v = quelle[k];
      if (typeof v === 'number') {
        return einheit === 'DN'
          ? { beschriftung, wert: `DN ${v.toLocaleString('de-DE')}` }
          : { beschriftung, wert: v, einheit, nachkomma: nk ?? 1 };
      }
      return { beschriftung, wert: v === null || v === undefined || v === '' ? null : `${String(v)}${einheit ? ` ${einheit}` : ''}` };
    });
}

/** Ein Reiter: Übersicht, je NEA ihre Id, Aggregat, Prüfpunkte, Abgleich, Fragen. */
type Reiter = string;

export function NeaAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<NeaStand | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [reiter, setReiter] = useState<Reiter>('uebersicht');
  const [laeuft, setLaeuft] = useState<'pdf' | 'excel' | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroNea(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  const herunterladen = async (art: 'pdf' | 'excel') => {
    setLaeuft(art);
    setMeldung(null);
    try {
      await (art === 'pdf' ? api.elektroNeaPdf(projektId) : api.elektroNeaExcel(projektId));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const leer = stand !== null && stand.anlagen.length === 0;

  const kopf = (
    <PageHeader
      titel="Elektro — Netzersatzanlagen"
      beschreibung={'Grundlagenmappe EG4: NEA-Bilanzen mit Nachrechnung, Aggregat und Tank, Auslastung in beiden '
        + 'Annahmen, Generator, Laufzeit bei Bilanzlast und die Prüfpunkte der Sicherheitsstromversorgung. Nur lesend.'}
      meta={stand && !leer ? `${stand.anlagen.length} NEA · Stand der Quelle ${datum(stand.anlagen[0].herkunft.stand)}` : undefined}
      aktionen={(
        <>
          <button type="button" className="m-knopf" disabled={!stand || leer || laeuft !== null}
                  onClick={() => herunterladen('excel')}>
            <IconHerunterladen size={13} /> Excel
          </button>
          <button type="button" className="m-knopf m-knopf-haupt" disabled={!stand || leer || laeuft !== null}
                  onClick={() => herunterladen('pdf')}
                  title="Nachweis ELT-NEA als PDF – neue Berichtsfassung nur bei geändertem Stand">
            <IconHerunterladen size={13} /> {laeuft === 'pdf' ? 'PDF wird erzeugt …' : 'Nachweis ELT-NEA (PDF)'}
          </button>
        </>
      )}
    />
  );

  if (fehler) return <div className="seite">{kopf}<ErrorState titel="Die Netzersatzanlagen konnten nicht geladen werden." technisch={fehler} /></div>;
  if (!stand) return <div className="seite">{kopf}<LoadingState text="Netzersatzanlagen werden geladen …" /></div>;
  if (leer) {
    return (
      <div className="seite">
        {kopf}
        <EmptyState titel="Keine Netzersatzanlage eingelesen" erklaerung="Eingelesen wird über das Werkzeug nea-einlesen." />
      </div>
    );
  }

  const varianten = stand.varianten as NeaAuslastung[];
  const je = varianten.filter((v) => v.variante === 'je NEA ein Aggregat');
  const gemeinsam = varianten.find((v) => v.variante === 'ein Aggregat für alle');
  const laufzeiten = stand.anlagen.map((a) => a.laufzeit?.laufzeitDatenblattH).filter((x): x is number => typeof x === 'number');
  const summe = stand.anlagen.reduce((s, a) => s + Number(a.bilanz.bedarfQuelleKw ?? a.bilanz.bedarfNachgerechnetKw ?? 0), 0);
  const maxJe = je.length > 0 ? Math.max(...je.map((v) => v.prozentKw ?? 0)) : null;

  const reiterListe: [Reiter, string][] = [
    ['uebersicht', 'Übersicht'],
    ...stand.anlagen.map((a) => [a.id, a.bezeichnung] as [Reiter, string]),
    ['aggregat', 'Aggregat und Tank'],
    ['pruefpunkte', 'Prüfpunkte SV'],
    ['abgleich', 'Abgleich mit dem Modell'],
    ['fragen', 'Offene Fragen'],
  ];

  const anlage = stand.anlagen.find((a) => a.id === reiter);

  return (
    <div className="seite">
      {kopf}
      <p className="ns-hinweisleiste">{PDF_HINWEIS}{meldung && <StatusBadge zustand="fehler" text={meldung} />}</p>

      <KpiReihe>
        <KpiCard titel="NEA-Bilanzen" wert={stand.anlagen.length} vergleich={stand.anlagen.map((a) => a.bezeichnung).join(', ')} />
        <KpiCard titel="Bedarf laut Quelle, Summe" wert={summe} einheit="kW" nachkomma={1} />
        <KpiCard titel="Auslastung je NEA ein Aggregat" wert={maxJe} einheit="%" nachkomma={1}
                 vergleich="höchste Auslastung" zustand={maxJe !== null && maxJe > 100 ? 'fehler' : 'gut'}
                 zustandText={maxJe !== null && maxJe > 100 ? 'über Nennleistung' : 'ausreichend'} />
        <KpiCard titel="Ein Aggregat für alle" wert={gemeinsam?.prozentKw ?? null} einheit="%" nachkomma={1}
                 vergleich="Annahme F1 – nicht entschieden"
                 zustand={gemeinsam?.ueberNennleistung ? 'fehler' : 'neutral'}
                 zustandText={gemeinsam?.ueberNennleistung ? 'über Nennleistung' : 'offen'} />
        <KpiCard titel="Laufzeit bei Bilanzlast" wert={laufzeiten.length > 0 ? Math.min(...laufzeiten) : null} einheit="h" nachkomma={1}
                 vergleich="kürzeste, be des Datenblatts" />
      </KpiReihe>

      <div className="reiter" role="tablist" style={{ flexWrap: 'wrap' }}>
        {reiterListe.map(([k, t]) => (
          <button key={k} type="button" role="tab" aria-selected={reiter === k}
                  className={`reiter-knopf${reiter === k ? ' aktiv' : ''}`} onClick={() => setReiter(k)}>
            {t}
          </button>
        ))}
      </div>

      {reiter === 'uebersicht' && <Uebersicht stand={stand} varianten={varianten} />}
      {anlage && <Anlage a={anlage} />}
      {reiter === 'aggregat' && <Aggregat a={stand.anlagen[0]} anlagen={stand.anlagen} />}
      {reiter === 'pruefpunkte' && <Pruefpunkte punkte={stand.pruefpunkte ?? []} />}
      {reiter === 'abgleich' && <Abgleich anlagen={stand.anlagen} />}
      {reiter === 'fragen' && (
        <ContentSection titel={`Offene Fragen an die Elektroplanung (${stand.offeneFragen.length})`}>
          <ul className="ns-liste">
            {stand.offeneFragen.map((f) => <li key={f.nr}><strong>{f.nr}</strong> {f.frage}</li>)}
          </ul>
        </ContentSection>
      )}
    </div>
  );
}

// ═══ Übersicht ════════════════════════════════════════════════════════════

function Uebersicht({ stand, varianten }: { stand: NeaStand; varianten: NeaAuslastung[] }) {
  return (
    <>
      <ContentSection titel="Auslastung in beiden Annahmen (F1)"
                      beschreibung="Ob jede NEA ihr eigenes Aggregat hat oder eines alle trägt, legt die Quelle nicht fest. Beide Zahlen stehen nebeneinander – keine ist die gültige.">
        <DataTable suche={false} spaltenwahl={false} dicht zeilen={varianten.map((v, i) => ({ ...v, nr: i }))}
                   schluessel={(v) => String(v.nr)}
                   spalten={[
                     { schluessel: 'variante', titel: 'Annahme', wert: (v) => v.variante },
                     { schluessel: 'bezug', titel: 'Bezug', wert: (v) => v.bezug },
                     { schluessel: 'last', titel: 'Last', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (v) => v.lastKw },
                     { schluessel: 'pkw', titel: 'Auslastung', einheit: '% von Pn', art: 'zahl', nachkomma: 1, wert: (v) => v.prozentKw },
                     { schluessel: 'pns', titel: 'mit nicht summiert', einheit: '%', art: 'zahl', nachkomma: 1, wert: (v) => v.prozentKwMitNichtSummiert },
                     { schluessel: 'pue', titel: 'gegen Überlast', einheit: '%', art: 'zahl', nachkomma: 1, wert: (v) => v.prozentKwGegenUeberlast },
                     { schluessel: 'pkva', titel: 'Scheinleistung', einheit: '% von Sn', art: 'zahl', nachkomma: 1, wert: (v) => v.prozentKva },
                     { schluessel: 'bewertung', titel: 'Bewertung', wert: (v) => (v.ueberUeberlast ? 'über Überlast' : v.ueberNennleistung ? 'über Nennleistung' : 'ausreichend'),
                       inhalt: (v) => <StatusBadge klein zustand={v.ueberNennleistung ? 'fehler' : 'gut'}
                                                   text={v.ueberUeberlast ? 'über Überlast' : v.ueberNennleistung ? 'über Nennleistung' : 'ausreichend'} /> },
                   ] as Spalte<NeaAuslastung & { nr: number }>[]} />
        <p><small>Pn = Nennleistung · cos φ des Datenblatts (Wirkleistung). „gegen Überlast“ bezieht die Last auf Pn zuzüglich der Überlast nach DIN 6280;
          „mit nicht summiert“ zählt die gelisteten, aber in keiner Summe enthaltenen Verbraucher hinzu (F2).</small></p>
      </ContentSection>

      <ContentSection titel="Netzersatzanlagen">
        <DataTable suche={false} spaltenwahl={false} dicht zeilen={stand.anlagen} schluessel={(a) => a.id}
                   spalten={[
                     { schluessel: 'bezeichnung', titel: 'NEA', wert: (a) => a.bezeichnung },
                     { schluessel: 'standort', titel: 'Standort laut Quelle', wert: (a) => a.geschossQuelle ?? '—' },
                     { schluessel: 'quelle', titel: 'Bedarf Quelle', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (a) => a.bilanz.bedarfQuelleKw as number },
                     { schluessel: 'nach', titel: 'nachgerechnet', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (a) => a.bilanz.bedarfNachgerechnetKw as number },
                     { schluessel: 'ns', titel: 'nicht summiert', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (a) => a.bilanz.nichtSummiertKw as number },
                     { schluessel: 's', titel: 'Scheinleistung', einheit: 'kVA', art: 'zahl', nachkomma: 1, wert: (a) => a.bilanz.scheinQuelleKva as number },
                     { schluessel: 'ib', titel: 'Ib', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (a) => a.bilanz.ibQuelleA as number },
                     { schluessel: 'lz', titel: 'Laufzeit', einheit: 'h', art: 'zahl', nachkomma: 1, wert: (a) => a.laufzeit?.laufzeitDatenblattH ?? null },
                     { schluessel: 'befunde', titel: 'Befunde', art: 'zahl', nachkomma: 0, wert: (a) => a.befunde.length },
                   ]} />
      </ContentSection>
    </>
  );
}

// ═══ Eine Anlage ══════════════════════════════════════════════════════════

function Pruefzeilen({ zeilen }: { zeilen: NeaPruefzeile[] }) {
  return (
    <DataTable suche={false} spaltenwahl={false} dicht zeilen={zeilen} schluessel={(z) => z.gegenstand}
               spalten={[
                 { schluessel: 'gegenstand', titel: 'Prüfung', wert: (z) => z.gegenstand },
                 { schluessel: 'soll', titel: 'Soll', wert: (z) => z.soll },
                 { schluessel: 'ist', titel: 'Ist', wert: (z) => z.ist },
                 { schluessel: 'bewertung', titel: 'Ergebnis', wert: (z) => pruefZustand(z.bewertung).text,
                   inhalt: (z) => { const p = pruefZustand(z.bewertung); return <StatusBadge klein zustand={p.zustand} text={p.text} />; } },
                 { schluessel: 'grundlage', titel: 'Grundlage', wert: (z) => z.grundlage },
               ]} />
  );
}

function Anlage({ a }: { a: NeaAnlage }) {
  const b = a.bilanz;
  const l = a.laufzeit;
  const verbraucher = a.abgaenge.filter((x) => x.ebene === 'verbraucher');
  return (
    <>
      <ContentSection titel={`${a.bezeichnung} (${a.kennzeichen})`}
                      beschreibung={[a.betriebsfall, a.geschossQuelle ? `Standort laut Quelle ${a.geschossQuelle}` : null,
                        `Quelle ${String(a.herkunft.quelleDatei ?? '—')}, Seite ${String(a.herkunft.quelleSeite ?? '—')}`].filter(Boolean).join(' · ')}>
        <KpiReihe>
          <KpiCard titel="Bedarf laut Quelle" wert={b.bedarfQuelleKw as number | null} einheit="kW" nachkomma={1}
                   vergleich={`nachgerechnet ${zahl(b.bedarfNachgerechnetKw, 1, 'kW')}`} />
          <KpiCard titel="Scheinleistung" wert={b.scheinQuelleKva as number | null} einheit="kVA" nachkomma={1}
                   vergleich={`Ib ${zahl(b.ibQuelleA, 1, 'A')}`} />
          <KpiCard titel="gelistet, nicht summiert" wert={b.nichtSummiertKw as number | null} einheit="kW" nachkomma={1} vergleich="F2" />
          <KpiCard titel="Auslastung, eigenes Aggregat" wert={b.auslastungKwProzent as number | null} einheit="%" nachkomma={1}
                   vergleich={`von ${zahl(a.wirkleistungKw, 0, 'kW')}`} />
          <KpiCard titel="Laufzeit bei Bilanzlast" wert={l?.laufzeitDatenblattH ?? null} einheit="h" nachkomma={1}
                   vergleich={l?.laufzeitTankblattH !== null && l?.laufzeitTankblattH !== undefined ? `mit Tankblatt-be ${zahl(l.laufzeitTankblattH, 1, 'h')}` : undefined} />
        </KpiReihe>

        {(a.generatorpruefung ?? []).length > 0 && (
          <>
            <h3>Generator und Abgang</h3>
            <Pruefzeilen zeilen={a.generatorpruefung ?? []} />
            {typeof a.generatorIk3dKa === 'number' && (
              <p><small>Kurzschlussstrom des Generators laut SIMARIS-Einspeisungsliste: Ik3D = {zahl(a.generatorIk3dKa, 1, 'kA')}.</small></p>
            )}
          </>
        )}

        {l && (
          <>
            <h3>Laufzeit des Tanks bei Bilanzlast</h3>
            <PropertyGrid spalten={3} dicht eintraege={[
              { beschriftung: 'Last der Bilanz', wert: l.lastKw, einheit: 'kW', nachkomma: 1, hinweis: `${zahl(l.lastProzent, 1, '%')} von Pn` },
              { beschriftung: 'Tankvolumen', wert: l.tankL, einheit: 'l', nachkomma: 1 },
              { beschriftung: 'Tankblatt rechnet mit', wert: l.betriebszeitTankblattH, einheit: 'h', nachkomma: 1, hinweis: 'auf seiner eigenen Leistungsbasis' },
              { beschriftung: 'be Datenblatt (interpoliert)', wert: l.beDatenblattGkwh, einheit: 'g/kWh', nachkomma: 1, herkunft: 'daten' },
              { beschriftung: 'Verbrauch mit be Datenblatt', wert: l.verbrauchDatenblattLh, einheit: 'l/h', nachkomma: 1 },
              { beschriftung: 'Laufzeit mit be Datenblatt', wert: l.laufzeitDatenblattH, einheit: 'h', nachkomma: 1 },
              { beschriftung: 'be Tankblatt (Richtwert)', wert: l.beTankblattGkwh, einheit: 'g/kWh', nachkomma: 1, herkunft: 'daten' },
              { beschriftung: 'Verbrauch mit be Tankblatt', wert: l.verbrauchTankblattLh, einheit: 'l/h', nachkomma: 1 },
              { beschriftung: 'Laufzeit mit be Tankblatt', wert: l.laufzeitTankblattH, einheit: 'h', nachkomma: 1 },
            ]} />
            <p className="ns-formel">t = V · ρ / (P · be){l.hinweis ? ` — ${l.hinweis}` : ''}</p>
          </>
        )}
      </ContentSection>

      {befundeGegliedert(a.befunde).length > 0 && (
        <ContentSection titel={`Befunde ${a.bezeichnung} (${a.befunde.length})`}>
          <div className="ns-raster">
            {befundeGegliedert(a.befunde).map((g) => (
              <div key={g.titel}>
                <h4>{g.titel}</h4>
                <ul className="ns-liste">{g.zeilen.map((z) => <li key={z}>{z}</li>)}</ul>
              </div>
            ))}
          </div>
        </ContentSection>
      )}

      <Bilanz a={a} verbraucher={verbraucher.length} />
    </>
  );
}

function Bilanz({ a, verbraucher }: { a: NeaAnlage; verbraucher: number }) {
  const ebene = (z: NeaAbgang) => (z.ebene === 'knoten' ? 0 : z.ebene === 'unterverteiler' ? 1
    : z.elternkennzeichen?.startsWith('NSHV') ? 1 : 2);
  const spalten: Spalte<NeaAbgang>[] = [
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', sortierbar: false, wert: (z) => z.bezeichnung,
      inhalt: (z) => (
        <span className={`ns-einzug${z.ebene === 'knoten' ? ' ns-einzug-wurzel' : ''}`} style={{ '--ns-ebene': ebene(z) } as CSSProperties}>
          {ebene(z) > 0 ? '› ' : ''}{z.bezeichnung}
        </span>
      ) },
    { schluessel: 'kennzeichen', titel: 'Kennzeichen', sortierbar: false, wert: (z) => z.kennzeichen ?? '' },
    { schluessel: 'standort', titel: 'Standort', sortierbar: false, wert: (z) => z.standort ?? '' },
    { schluessel: 'p', titel: 'P', einheit: 'kW', art: 'zahl', nachkomma: 1, sortierbar: false, wert: (z) => z.pQuelleKw },
    { schluessel: 'g', titel: 'g', art: 'zahl', nachkomma: 2, sortierbar: false, wert: (z) => z.gleichzeitigkeit, titelHinweis: 'Gleichzeitigkeitsfaktor laut Quelle' },
    { schluessel: 'ib', titel: 'Ib', einheit: 'A', art: 'zahl', nachkomma: 1, sortierbar: false, wert: (z) => z.ibA },
    { schluessel: 's', titel: 'S', einheit: 'kVA', art: 'zahl', nachkomma: 1, sortierbar: false, wert: (z) => z.sKva },
    { schluessel: 'summe', titel: 'in Summe', sortierbar: false, wert: (z) => (z.inQuellsumme === null ? 'offen' : z.inQuellsumme ? 'ja' : 'nein'),
      inhalt: (z) => <StatusBadge klein zustand={z.inQuellsumme === false ? 'warnung' : z.inQuellsumme === null ? 'neutral' : 'gut'}
                                  text={z.inQuellsumme === null ? 'offen' : z.inQuellsumme ? 'ja' : 'nein'} /> },
    { schluessel: 'modell', titel: 'im Modell', sortierbar: false, wert: (z) => neaBefundText(z.befund) },
  ];
  return (
    <ContentSection titel={`NEA-Bilanz ${a.bezeichnung}: Knoten › Unterverteiler › Verbraucher`}
                    beschreibung={`${a.abgaenge.length} Zeilen der Quelle, davon ${verbraucher} Verbraucher. „in Summe“: ob die Zahl in der Summe darüber enthalten ist; „offen“ – die Zahlen sagen es nicht eindeutig.`}>
      <DataTable dicht zeilen={a.abgaenge} spalten={spalten} schluessel={(z) => `${z.reihenfolge}-${z.kennzeichen ?? ''}`}
                 ausfuhrDateiname={`NEA_Bilanz_${a.kennzeichen}`} />
    </ContentSection>
  );
}

// ═══ Aggregat, Prüfpunkte, Abgleich ═══════════════════════════════════════

function Aggregat({ a, anlagen }: { a: NeaAnlage; anlagen: NeaAnlage[] }) {
  return (
    <div className="ns-raster">
      <ContentSection titel="Aggregat – Datenblatt" beschreibung={`${String(a.herkunft.datenblattDatei ?? 'Datenblatt')}; ${String(a.datenblatt.fehlend ?? 0)} Werte nicht gefunden`}>
        <PropertyGrid spalten={1} dicht eintraege={[
          ...werte(a.datenblatt, DATENBLATT),
          { beschriftung: 'Wirkleistung Pn = Sn · cos φ', wert: a.wirkleistungKw ?? null, einheit: 'kW', nachkomma: 0, hinweis: 'berechnet' },
        ]} />
      </ContentSection>
      <ContentSection titel="Tanknachweis" beschreibung={`${String(a.herkunft.tankDatei ?? 'Tankblatt')}; ${String(a.tank.fehlend ?? 0)} Werte nicht gefunden`}>
        <PropertyGrid spalten={1} dicht eintraege={werte(a.tank, TANK)} />
        <p className="ns-formel">V = P · t · be / ρ</p>
        <p><small>Alle NEA rechnen mit demselben Aggregat- und Tankblatt ({anlagen.length} Bilanzen). Welcher Kraftstoffwert und welche
          Leistungsbasis gelten, ist F4.</small></p>
      </ContentSection>
      <ContentSection titel="Herkunft">
        <PropertyGrid spalten={1} dicht eintraege={[
          { beschriftung: 'Herkunft', wert: String(a.herkunft.herkunft ?? '—') },
          { beschriftung: 'Stand der Quelle', wert: datum(a.herkunft.stand) },
          { beschriftung: 'eingelesen', wert: zeitpunkt(a.herkunft.erstelltAm) },
          { beschriftung: 'zuletzt geändert', wert: zeitpunkt(a.herkunft.geaendertAm) },
        ]} />
      </ContentSection>
    </div>
  );
}

function Pruefpunkte({ punkte }: { punkte: NeaPruefpunkt[] }) {
  return (
    <ContentSection titel="Sicherheitsstromversorgung – Prüfpunkte"
                    beschreibung="„Daten fehlen“: Die Quelle trägt die Angabe nicht – es wird weder eine Anforderung noch ein Wert angenommen. „offen“: rechenbar, aber die Bewertung hängt an einer offenen Fachfrage.">
      <DataTable suche={false} spaltenwahl={false} dicht zeilen={punkte} schluessel={(p) => p.nr}
                 spalten={[
                   { schluessel: 'nr', titel: 'Nr.', wert: (p) => p.nr },
                   { schluessel: 'gegenstand', titel: 'Prüfpunkt', wert: (p) => p.gegenstand },
                   { schluessel: 'grundlage', titel: 'Grundlage', wert: (p) => p.grundlage },
                   { schluessel: 'stand', titel: 'Stand', wert: (p) => pruefZustand(p.stand).text,
                     inhalt: (p) => { const z = pruefZustand(p.stand); return <StatusBadge klein zustand={z.zustand} text={z.text} />; } },
                   { schluessel: 'text', titel: 'Erläuterung', wert: (p) => p.text },
                 ]} />
    </ContentSection>
  );
}

function Abgleich({ anlagen }: { anlagen: NeaAnlage[] }) {
  const zeilen = anlagen.flatMap((a) => a.abgaenge
    .filter((x) => x.ebene !== 'verbraucher' && x.kennzeichen)
    .map((x) => ({ ...x, nea: a.bezeichnung })));
  return (
    <ContentSection titel="Abgleich mit dem Modell"
                    beschreibung="Über das Kennzeichen, mit den Regeln der Leistungsbilanz (EG1). Das Vorwort „NSHV_“ wird nicht abgeschnitten. Meldungen, keine Entscheidungen (F5, F6).">
      <DataTable dicht zeilen={zeilen} schluessel={(z) => `${z.nea}-${z.reihenfolge}`} ausfuhrDateiname="NEA_Abgleich_Modell"
                 spalten={[
                   { schluessel: 'kennzeichen', titel: 'Kennzeichen', wert: (z) => z.kennzeichen },
                   { schluessel: 'stufe', titel: 'Stufe', wert: (z) => (z.ebene === 'knoten' ? 'Knoten (NSHV_SV)' : 'Unterverteiler') },
                   { schluessel: 'nea', titel: 'NEA', wert: (z) => z.nea },
                   { schluessel: 'befund', titel: 'Befund', wert: (z) => neaBefundText(z.befund),
                     inhalt: (z) => <StatusBadge klein zustand={z.befund?.startsWith('nicht') ? 'warnung' : 'neutral'} text={neaBefundText(z.befund) || '—'} /> },
                   { schluessel: 'versorgung', titel: 'Versorgung im Modell', wert: (z) => z.modellVersorgungsart ?? '—' },
                 ] as Spalte<NeaAbgang & { nea: string }>[]} />
    </ContentSection>
  );
}
